<template>
  <div data-testid="live-feed">
    <LoadingIndicator v-if="loading" />
    <cms-live-feed
      v-else-if="!!content.length"
      :theme="themeSettings"
      :content="generateDynamicTimeAgoValues(content)"
      :livefeed-config="config"
      :component-id="componentId"
      :fallback-image="fallbackImage"
      :path-prefix="pathPrefix"
      :logo="watermarkLogo"
      @save-prev-focused-elem="savePrevFocusedElem($event)"
      @toggle-lightbox="toggleLightbox($event)"
    />
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import LoadingIndicator from '../../global/LoadingIndicator.vue';
import HomepageFetchHelper from '../../../helpers/homepage-fetcher.js';
import { getTimeAgoStringFromUTC } from '../../../helpers/time-ago-in-words.js';

defineOptions({
  name: 'ViewLiveFeed',
});

const props = defineProps({
  containerIdx: {
    type: Number,
    default: null,
  },
  componentIdx: {
    type: Number,
    default: null,
  },
  containerId: {
    type: String,
    default: null,
  },
});

const store = useStore();

/** Captured in onMounted so async fetch paths keep the same clients after await (getCurrentInstance() is null after await). */
const nuxtGlobs = { axios: null, staticFetch: null, t: null };

const loading = ref(true);
const content = ref([]);
const config = ref({});

const homepage = computed(() => store.state.homepage);
const theme = computed(() => store.state.theme);
const pathPrefix = computed(() => store.state.pathPrefix);
const isThemePreview = computed(() => store.state.isThemePreview);
const featureFlags = computed(() => store.state.featureFlags);
const globals = computed(() => store.state.globals);
const settings = computed(() => store.state.settings);
const staticFilesConfig = computed(() => store.state.staticFilesConfig);
const mediaPreviewContent = computed(() => store.state.mediaPreviewContent);

const getLiveFeedApi = computed(() => store.getters.getLiveFeedApi);
const getS3UrlForMedia = computed(() => store.getters.getS3UrlForMedia);
const getGlobalThemeSettings = computed(() => store.getters.getGlobalThemeSettings);
const getComponentTranslation = computed(() => store.getters['translation/getComponentTranslation']);

const defaultData = computed(() => mediaPreviewContent.value?.liveFeedPreviewContent || []);

const themeSettings = computed(() => {
  if (!theme.value?.containers) return {};
  return {
    ...theme.value.containers[props.containerIdx].components[props.componentIdx].options,
    translations: getComponentTranslation.value('live_feed'),
    globalThemeSettings: getGlobalThemeSettings.value,
  };
});

const componentId = computed(() => {
  if (isThemePreview.value) {
    return 'preview_id';
  }

  return `${props.containerId}_${props.componentIdx}`;
});

const useS3ForMedia = computed(() => featureFlags.value.cms_stored_data_live_feed_enabled);

const watermarkLogo = computed(() => globals.value.watermark?.url || globals.value.logo.url);

const fallbackImage = computed(() => {
  const liveFeedDefaultFallbackImage =
    globals.value?.fallback_images?.find((item) => item.feature === 'live_feed') || {};
  const settingsVal = settings.value || {};
  const liveFeedFallbackImage = settingsVal.liveFeedFallbackImage || {};
  return Object.keys(liveFeedFallbackImage).length === 0 ? liveFeedDefaultFallbackImage : liveFeedFallbackImage;
});

async function fetchContentFromS3AndValidate() {
  try {
    const s3UrlForContent = getS3UrlForMedia.value('live_feed');
    const contentResponse = await nuxtGlobs.staticFetch.get(s3UrlForContent);

    const lastS3FileUpdate = contentResponse.data?.meta?.last_static_update;
    const lastMediaUpdate =
      staticFilesConfig.value?.last_static_update?.live_feeds ||
      staticFilesConfig.value?.homepage?.last_static_update?.live_feeds;

    const isStaticDataFresh = HomepageFetchHelper.validateLastStaticUpdate(lastS3FileUpdate, lastMediaUpdate);
    const liveFeedPosts = contentResponse?.data?.live_feeds;
    if (!isStaticDataFresh || !liveFeedPosts) {
      return await fetchContentFromThrillshare();
    }

    return liveFeedPosts;
  } catch (e) {
    console.error(e);
    return await fetchContentFromThrillshare();
  }
}

async function fetchContentFromThrillshare() {
  try {
    const { url, params } = getLiveFeedApi.value(nuxtGlobs.axios.defaults.baseURL);
    const contentResponse = await nuxtGlobs.axios.get(url, { params, cache: { ignoreCache: false } });

    return contentResponse?.data?.live_feeds || [];
  } catch (e) {
    console.error(e);
    return [];
  }
}

function generateDynamicTimeAgoValues(liveFeeds) {
  if (isThemePreview.value) {
    return liveFeeds;
  }
  return liveFeeds?.map((lf) => {
    return {
      ...lf,
      time_ago: getTimeAgoStringFromUTC(lf.time, nuxtGlobs.t),
    };
  });
}

function setupThemePreview() {
  content.value = defaultData.value.map((lf) => ({
    ...lf,
    avatar: lf.avatar || globals.value.logo.url,
  }));
  loading.value = false;
}

async function getContent() {
  if (isThemePreview.value) {
    setupThemePreview();
    return;
  }

  if (useS3ForMedia.value) {
    content.value = await fetchContentFromS3AndValidate();
  } else {
    content.value = await fetchContentFromThrillshare();
  }
  loading.value = false;
}

function savePrevFocusedElem(target) {
  store.commit('setPrevFocusedElem', target);
}

function toggleLightbox(imgObj) {
  store.commit('setPrevFocusedElem', imgObj.target);
  store.commit('setLightBoxCurrentIndex', imgObj.idx || 0);
  store.commit('openLightbox');
  store.commit('setLightboxImages', imgObj.images);
}

onMounted(() => {
  const inst = getCurrentInstance();
  const p = inst?.proxy;
  const gp = inst?.appContext.config.globalProperties;
  nuxtGlobs.axios = p?.$axios ?? gp?.$axios;
  nuxtGlobs.staticFetch = p?.$staticFetch ?? gp?.$staticFetch;
  nuxtGlobs.t = p?.$t ?? gp?.$t;
  config.value = homepage.value.live_feed || {};
  getContent();
});

defineExpose({
  content,
  config,
  loading,
  getContent,
  setupThemePreview,
});
</script>
